import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { Link, useNavigate } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Layers, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui";
import { PageFrame } from "@/components/common";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import {
  DataTable,
  DataTablePrimaryText,
  queryLoadState,
  type DataTableColumn,
} from "@/components/table";
import { cn } from "@/lib/utils";
import {
  pageSkip,
  serverPageWindow,
  useClampUrlPage,
  useUrlPage,
} from "@/hooks/useUrlPage";
import {
  adminPaymentAuditKeys,
  adminPaymentAuditService,
  type AdminEconomicEventSummaryRow,
} from "@/services/adminPaymentAuditService";
import { formatDateTime } from "@/lib/datetime";
import { formatMoney } from "@/lib/money";
import { idPrefix } from "./formatters";
import {
  economicAllocationMethodLabel,
  economicEventStatusLabel,
  economicEventTypeLabel,
} from "@/lib/economicEventLabels";
import { StatusBadge } from "./shared";

/**
 * Rows arrive newest first from the server; columns are not client-sortable because
 * sorting one page would misrepresent the full list.
 */
type ServerOrderColumn = "created";

const PAGE_SIZE = 50;

export default function AdminPaymentsEventsListPage() {
  const { t } = useTranslation("payments");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const navigate = useNavigate();
  const [page, setPage] = useUrlPage();
  const listParams = { skip: pageSkip(page, PAGE_SIZE), limit: PAGE_SIZE };

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    isPlaceholderData,
  } = useQuery({
    queryKey: adminPaymentAuditKeys.eventList(listParams),
    queryFn: () => adminPaymentAuditService.listEconomicEvents(listParams),
    placeholderData: keepPreviousData,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  useClampUrlPage(
    page,
    data && !isPlaceholderData
      ? serverPageWindow(page, PAGE_SIZE, total).totalPages
      : undefined,
  );

  const columns = useMemo<
    DataTableColumn<AdminEconomicEventSummaryRow, ServerOrderColumn>[]
  >(
    () => [
      {
        id: "created",
        header: t("shared.colCreated"),
        defaultWidth: 160,
        cell: (row) => (
          <span className="break-words tabular-nums text-surface-700">
            {formatDateTime(row.created_at)}
          </span>
        ),
      },
      {
        id: "status",
        header: t("shared.colStatus"),
        defaultWidth: 120,
        cell: (row) => (
          <StatusBadge
            status={row.status}
            label={economicEventStatusLabel(row.status, t)}
          />
        ),
      },
      {
        id: "gross",
        header: t("eventsList.colGross"),
        defaultWidth: 120,
        cell: (row) => (
          <DataTablePrimaryText className="tabular-nums">
            {formatMoney(row.gross_amount_cents, row.currency)}
          </DataTablePrimaryText>
        ),
      },
      {
        id: "type",
        header: t("shared.colType"),
        defaultWidth: 160,
        cell: (row) => (
          <span className="break-words text-surface-700">
            {economicEventTypeLabel(row.type, t)}
          </span>
        ),
      },
      {
        id: "method",
        header: t("eventsList.colMethod"),
        defaultWidth: 180,
        cell: (row) => (
          <span className="break-words text-surface-600">
            {economicAllocationMethodLabel(row.allocation_method, t)}
          </span>
        ),
      },
      {
        id: "externalPayment",
        header: t("eventsList.colExternalPayment"),
        defaultWidth: 140,
        cell: (row) =>
          row.external_payment_id ? (
            <Link
              to={`/admin/payments/external-payments?paymentId=${row.external_payment_id}`}
              className={cn(
                "inline-flex w-fit items-center gap-1.5 text-sm transition-colors",
                "rounded-md text-surface-700 hover:text-primary-700",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500",
              )}
              title={row.external_payment_id}
              aria-label={t("eventDetail.viewExternalPayment")}
              onClick={(event) => event.stopPropagation()}
            >
              <ClickToCopyId
                value={row.external_payment_id}
                displayValue={idPrefix(row.external_payment_id)}
                className="text-xs text-surface-700"
              />
              <ArrowUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </Link>
          ) : (
            <span className="font-mono text-xs text-surface-600">
              {t("shared.emptyCell")}
            </span>
          ),
      },
      {
        id: "lines",
        header: t("eventsList.colLines"),
        defaultWidth: 80,
        cell: (row) => (
          <span className="tabular-nums text-surface-700">
            {row.allocation_line_count}
          </span>
        ),
      },
    ],
    [t],
  );

  return (
    <PageFrame
      title={t("layout.tabEvents")}
      description={t("layout.eventsDescription")}
      icon={Layers}
      actions={
        <Button
          type="button"
          variant="outline"
          isLoading={isFetching}
          leftIcon={<RefreshCw className="h-4 w-4" />}
          onClick={() => void refetch()}
        >
          {tCommon("refresh")}
        </Button>
      }
    >
      <DataTable
        labels={tableLabels}
        closeLabel={tCommon("close")}
        loadingLabel={tCommon("loading")}
        rows={items}
        columns={columns}
        getRowKey={(row) => row.id}
        defaultSortColumn="created"
        defaultSortDirection="desc"
        serverPagination={{
          page,
          pageSize: PAGE_SIZE,
          total,
          onPageChange: setPage,
          isFetching,
        }}
        preferencesKey="admin-payment-events"
        loadState={queryLoadState({
          isLoading,
          error,
          errorMessage: t("eventsList.loadError"),
          retryLabel: tCommon("tryAgain"),
          onRetry: () => void refetch(),
        })}
        emptyMessage={t("shared.emptyCell")}
        onRowClick={(row) =>
          void navigate(`/admin/payments/events/${row.id}`)
        }
        openRowLabel={(row) => t("eventsList.openRowAria", { id: row.id })}
      />
    </PageFrame>
  );
}
